package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.formatIsoDateTime
import br.com.vvrefeicoes.portal.core.util.parallelMap
import br.com.vvrefeicoes.portal.domain.model.Permission
import br.com.vvrefeicoes.portal.domain.model.PermissionAction
import br.com.vvrefeicoes.portal.domain.model.UserAccount
import br.com.vvrefeicoes.portal.domain.model.UserImportPreview
import br.com.vvrefeicoes.portal.domain.model.UserImportResult
import br.com.vvrefeicoes.portal.domain.model.UserImportRow
import br.com.vvrefeicoes.portal.domain.model.UserProfile
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.UserCandidate
import br.com.vvrefeicoes.portal.domain.repository.UserPage
import br.com.vvrefeicoes.portal.domain.repository.UserRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteUserRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : UserRepository {
    private val permissionIds = mutableMapOf<String, String>()

    override suspend fun loadUsers(): List<UserAccount> = loadUsersPage(0, 100).users

    override suspend fun loadUsersPage(
        page: Int,
        size: Int,
        query: String,
        onlyActive: Boolean,
    ): UserPage {
        val path =
            buildString {
                append("/users?page=$page&size=$size")
                if (onlyActive) append("&status=true")
                if (query.isNotBlank()) append("&q=").append(query.encodeURLParameter())
            }
        val response = get<PageDto<UserDto>>(path)
        val users =
            response.content.parallelMap { user ->
                user.toDomain(loadPermissionMap("/users/${user.id}/permissions"))
            }
        return UserPage(users, response.page.totalPages)
    }

    override suspend fun loadUserCandidates(onlyActive: Boolean): List<UserCandidate> {
        // Só id/nome (sem permissões): pagina todas as páginas do /users.
        val status = if (onlyActive) "&status=true" else ""
        val firstPage = get<PageDto<UserDto>>("/users?page=0&size=100$status")
        val remaining =
            (1 until firstPage.page.totalPages).parallelMap { page ->
                get<PageDto<UserDto>>("/users?page=$page&size=100$status").content
            }
        return (firstPage.content + remaining.flatten())
            .map { UserCandidate(id = it.id, name = it.nome, role = it.cargo.orEmpty()) }
    }

    override suspend fun upsert(user: UserAccount) {
        if (!isUuid(user.id)) {
            throw IllegalStateException("O registro remoto de usuário exige uma senha inicial.")
        }
        patch<UserDto>(
            "/users/${user.id}",
            UserUpdateDto(
                nome = user.fullName,
                username = user.email.substringBefore('@'),
                email = user.email,
                telcel = user.phone,
                idRoles = user.profileId?.takeIf(::isUuid),
                setor = user.sector,
                cargo = user.cargo,
            ),
        )
    }

    override suspend fun create(
        user: UserAccount,
        password: String,
    ): String =
        post<UserDto>(
            "/users",
            UserCreateDto(
                nome = user.fullName,
                username = user.email.substringBefore('@'),
                password = password,
                email = user.email,
                telcel = user.phone,
                idRoles = user.profileId?.takeIf(::isUuid),
                setor = user.sector,
                cargo = user.cargo,
            ),
        ).id

    override suspend fun importUsers(
        profileId: String,
        rows: List<UserImportRow>,
    ): UserImportResult =
        post<UserImportResponseDto>(
            "/users/import",
            UserImportRequestDto(
                profileId = profileId,
                rows = rows.map { UserImportRowDto(it.name, it.email, it.sector, it.cargo, it.phone) },
            ),
        ).toDomain()

    override suspend fun previewImport(rows: List<UserImportRow>): UserImportPreview =
        post<UserImportPreviewResponseDto>(
            "/users/import/preview",
            UserImportPreviewRequestDto(
                rows = rows.map { UserImportRowDto(it.name, it.email, it.sector, it.cargo, it.phone) },
            ),
        ).toDomain()

    override suspend fun changeStatus(
        userId: String,
        active: Boolean,
    ) {
        patch<UserDto>(
            "/users/$userId/status",
            UserStatusDto(status = active),
        )
    }

    override suspend fun changePassword(
        userId: String,
        newPassword: String,
    ) {
        patchNoContent("/users/$userId/password", UserPasswordDto(novaSenha = newPassword))
    }

    override suspend fun replaceUserPermissions(
        userId: String,
        permissions: Map<String, Set<PermissionAction>>,
    ) {
        val permissionGrants =
            permissions.mapNotNull { (permission, actions) ->
                permissionId(permission)?.let { id ->
                    PermissionGrantDto(idPermissions = id, actions = actions)
                }
            }
        put<Unit>("/users/$userId/permissions", PermissionGrantsDto(permissionGrants))
    }

    override suspend fun loadProfiles(): List<UserProfile> =
        get<PageDto<RoleDto>>("/roles?page=0&size=100").content.parallelMap { role ->
            UserProfile(role.id, role.tituloRoles, loadPermissionMap("/roles/${role.id}/permissions"))
        }

    override suspend fun upsertProfile(profile: UserProfile) {
        val roleId =
            if (isUuid(profile.id)) {
                patch<RoleDto>("/roles/${profile.id}", RoleUpdateDto(profile.name)).id
            } else {
                post<RoleDto>("/roles", RoleCreateDto(profile.name)).id
            }
        val permissionGrants =
            profile.permissions.mapNotNull { (permission, actions) ->
                permissionId(permission)?.let { id ->
                    PermissionGrantDto(idPermissions = id, actions = actions)
                }
            }
        put<Unit>(
            "/roles/$roleId/permissions",
            PermissionGrantsDto(
                permissionGrants,
            ),
        )
    }

    override suspend fun removeProfile(id: String) {
        if (isUuid(id)) delete("/roles/$id")
    }

    override suspend fun loadPermissions(): List<Permission> =
        get<PageDto<PermissionDto>>("/permissions?page=0&size=100").content.map {
            permissionIds[it.nome] = it.id
            Permission(it.nome)
        }

    override suspend fun upsertPermission(permission: Permission) {
        val backendId = permissionIds[permission.id]
        if (backendId != null) {
            patch<PermissionDto>("/permissions/$backendId", PermissionRequestDto(permission.id))
        } else {
            val created = post<PermissionDto>("/permissions", PermissionRequestDto(permission.id))
            permissionIds[permission.id] = created.id
        }
    }

    override suspend fun removePermission(id: String) {
        permissionIds[id]?.let { delete("/permissions/$it") }
    }

    private suspend fun loadPermissionMap(path: String): Map<String, Set<PermissionAction>> =
        get<List<PermissionGrantDto>>(path).filter { it.concedida }.associate { grant ->
            grant.nome to grant.actions
        }

    private suspend fun permissionId(code: String): String? =
        permissionIds[code] ?: run {
            loadPermissions()
            permissionIds[code]
        }

    private suspend inline fun <reified T> get(path: String): T =
        request {
            httpClient.get(baseUrl + path) {
                authorize()
            }
        }

    private suspend inline fun <reified T> post(
        path: String,
        body: Any,
    ): T = request { httpClient.post(baseUrl + path) { jsonBody(body) } }

    private suspend inline fun <reified T> patch(
        path: String,
        body: Any,
    ): T = request { httpClient.patch(baseUrl + path) { jsonBody(body) } }

    private suspend inline fun <reified T> put(
        path: String,
        body: Any,
    ): T = request { httpClient.put(baseUrl + path) { jsonBody(body) } }

    private suspend fun delete(path: String) {
        val response = httpClient.delete(baseUrl + path) { authorize() }
        check(response.status.isSuccess()) { "HTTP ${response.status.value} em DELETE $path" }
    }

    private suspend fun patchNoContent(
        path: String,
        body: Any,
    ) {
        val response = httpClient.patch(baseUrl + path) { jsonBody(body) }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<ApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
    }

    private suspend inline fun <reified T> request(crossinline call: suspend () -> io.ktor.client.statement.HttpResponse): T {
        val response = call()
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<ApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun io.ktor.client.request.HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }

    private fun isUuid(value: String): Boolean = value.matches(Regex("[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}"))
}

@Serializable
private data class ApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class PageDto<T>(
    val content: List<T> = emptyList(),
    val page: PageMetadata = PageMetadata(),
)

@Serializable
private data class PageMetadata(
    val totalPages: Int = 1,
)

@Serializable
private data class UserDto(
    val id: String,
    val nome: String,
    val username: String,
    val email: String,
    val telcel: String? = null,
    val setor: String? = null,
    val cargo: String? = null,
    val status: Boolean = true,
    val idRoles: String? = null,
    val tituloRoles: String? = null,
    val updatedAt: String? = null,
    val dateAcs: String? = null,
    val updatedByName: String? = null,
    val companies: List<UserCompanyDto> = emptyList(),
)

@Serializable
private data class UserCompanyDto(
    val id: String,
    val name: String,
)

private fun UserDto.toDomain(permissions: Map<String, Set<PermissionAction>>) =
    UserAccount(
        id = id,
        fullName = nome,
        role = tituloRoles.orEmpty(),
        cargo = cargo.orEmpty(),
        companyNames = companies.map { it.name },
        sector = setor.orEmpty(),
        phone = telcel.orEmpty(),
        email = email,
        profileId = idRoles,
        permissions = permissions,
        updatedBy = updatedByName.orEmpty(),
        updatedAt = formatIsoDateTime(updatedAt),
        lastAccess = formatIsoDateTime(dateAcs),
        active = status,
    )

@Serializable
private data class RoleDto(
    val id: String,
    val tituloRoles: String,
)

@Serializable
private data class PermissionDto(
    val id: String,
    val nome: String,
)

@Serializable
private data class PermissionGrantDto(
    val idPermissions: String,
    val nome: String = "",
    val actions: Set<PermissionAction> = emptySet(),
    val concedida: Boolean = true,
)

@Serializable
private data class PermissionGrantsDto(
    val permissions: List<PermissionGrantDto>,
)

@Serializable
private data class PermissionRequestDto(
    val nome: String,
)

@Serializable
private data class RoleCreateDto(
    val tituloRoles: String,
)

@Serializable
private data class RoleUpdateDto(
    val tituloRoles: String,
)

@Serializable
private data class UserUpdateDto(
    val nome: String,
    val username: String,
    val email: String,
    val telcel: String,
    val idRoles: String?,
    val setor: String,
    val cargo: String,
)

@Serializable
private data class UserCreateDto(
    val nome: String,
    val username: String,
    val password: String,
    val email: String,
    val telcel: String,
    val idRoles: String?,
    val setor: String,
    val cargo: String,
)

@Serializable
private data class UserStatusDto(
    val status: Boolean,
)

@Serializable
private data class UserImportRequestDto(
    val profileId: String,
    val rows: List<UserImportRowDto>,
)

@Serializable
private data class UserImportPreviewRequestDto(
    val rows: List<UserImportRowDto>,
)

@Serializable
private data class UserImportRowDto(
    val name: String,
    val email: String,
    val sector: String,
    val cargo: String,
    val phone: String,
)

@Serializable
private data class UserImportResponseDto(
    val totalRows: Int = 0,
    val created: Int = 0,
    val skippedMissingEmail: Int = 0,
    val skippedDuplicate: Int = 0,
    val skippedExisting: Int = 0,
    val invalid: Int = 0,
    val errors: List<String> = emptyList(),
) {
    fun toDomain() =
        UserImportResult(
            totalRows = totalRows,
            created = created,
            skippedMissingEmail = skippedMissingEmail,
            skippedDuplicate = skippedDuplicate,
            skippedExisting = skippedExisting,
            invalid = invalid,
            errors = errors,
        )
}

@Serializable
private data class UserImportPreviewResponseDto(
    val totalRows: Int = 0,
    val ready: Int = 0,
    val skippedMissingEmail: Int = 0,
    val skippedDuplicate: Int = 0,
    val skippedExisting: Int = 0,
    val invalid: Int = 0,
) {
    fun toDomain() =
        UserImportPreview(
            totalRows = totalRows,
            ready = ready,
            skippedMissingEmail = skippedMissingEmail,
            skippedDuplicate = skippedDuplicate,
            skippedExisting = skippedExisting,
            invalid = invalid,
        )
}

@Serializable
private data class UserPasswordDto(
    val senhaAtual: String? = null,
    val novaSenha: String,
)
